import React, { useMemo, useState } from "react";
import { mdiCheckboxBlankCircleOutline, mdiCheckboxMarkedCircle, mdiClose, mdiMagnify, mdiPlusCircleOutline } from "@mdi/js";
import MdiIcon from "../../../components/device/app/MdiIcon";
import Sheet from "../../../components/layout/app/Sheet";
import { deviceIcon } from "../../../components/device/app/deviceTheme";
import { actionKind } from "../../../automation/actions";
import { Chips, Segmented } from "./controls";

const roomOf = (device) => [device?.room, device?.floor].filter(Boolean).join(" · ") || "Other";

// Groups devices by "Room · Floor", in the order they come
function byRoom(items, homeDevice) {
  const groups = [];
  items.forEach((item) => {
    const title = roomOf(homeDevice(item.applianceId));
    const group = groups.find((g) => g.title === title);
    if (group) group.items.push(item);
    else groups.push({ title, items: [item] });
  });
  return groups;
}

const DeviceIcon = ({ device }) => (
  <span className="hx-auto-device-pic" aria-hidden="true">
    <MdiIcon path={deviceIcon(device?.deviceType, true)} size={24} />
  </span>
);

// Every device the automation can control, to add one or more
function DevicePicker({ open, devices, homeDevice, onAdd, onClose }) {
  const [picked, setPicked] = useState([]);
  const [query, setQuery] = useState("");
  const shown = devices.filter((d) => d.name.toLowerCase().includes(query.trim().toLowerCase()));
  const toggle = (id) => setPicked((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
  const close = (add) => {
    if (add && picked.length) onAdd(picked);
    setPicked([]);
    setQuery("");
    onClose();
  };
  return (
    <Sheet open={open} onClose={() => close(false)} title="Add devices">
      <div className="hx-auto-sheet">
        <label className="hx-auto-search">
          <MdiIcon path={mdiMagnify} size={20} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search devices" aria-label="Search devices" />
        </label>
        <div className="hx-auto-picker">
          {byRoom(shown, homeDevice).map((group) => (
            <section key={group.title} aria-label={group.title}>
              <h3 className="hx-auto-room">{group.title}</h3>
              {group.items.map((device) => {
                const selected = picked.includes(device.applianceId);
                return (
                  <button
                    key={device.applianceId}
                    type="button"
                    role="checkbox"
                    aria-checked={selected}
                    className="hx-auto-pick-row"
                    onClick={() => toggle(device.applianceId)}
                  >
                    <DeviceIcon device={device} />
                    <span>{device.name}</span>
                    <MdiIcon path={selected ? mdiCheckboxMarkedCircle : mdiCheckboxBlankCircleOutline} size={24} />
                  </button>
                );
              })}
            </section>
          ))}
          {!shown.length ? <p className="hx-auto-none">No devices to add.</p> : null}
        </div>
        <button type="button" className="hx-btn hx-auto-sheet-button" onClick={() => close(true)} disabled={!picked.length}>
          {picked.length ? `Add ${picked.length}` : "Add"}
        </button>
      </div>
    </Sheet>
  );
}

// Step "Then do this": the devices it controls and what each does
// (app/screens/Automation/create/ActionsStep.js)
export default function ActionsStep({ draft, onActions, eligible, homeDevice, capsDevice, allowToggle }) {
  const [picking, setPicking] = useState(false);
  const chosen = draft.actions;
  const unused = useMemo(
    () => eligible.filter((device) => !chosen.some((action) => action.applianceId === device.applianceId)),
    [eligible, chosen]
  );

  const add = (ids) => {
    const added = ids.map((applianceId) => {
      const kind = actionKind(homeDevice(applianceId));
      return { applianceId, choice: kind?.choices[0].key || "on", level: kind?.level?.initial };
    });
    onActions([...chosen, ...added]);
  };
  const update = (applianceId, changes) =>
    onActions(chosen.map((action) => (action.applianceId === applianceId ? { ...action, ...changes } : action)));
  const remove = (applianceId) => onActions(chosen.filter((action) => action.applianceId !== applianceId));

  return (
    <>
      {byRoom(chosen, homeDevice).map((group) => (
        <section key={group.title} aria-label={group.title}>
          <h3 className="hx-auto-room">{group.title}</h3>
          {group.items.map((action) => {
            const device = homeDevice(action.applianceId);
            const caps = capsDevice(action.applianceId);
            const kind = actionKind(device);
            const name = device?.name || caps?.name || "Device";
            const choices = [...(kind?.choices || []), ...(allowToggle && caps?.toggle ? [{ key: "toggle", label: "TOGGLE" }] : [])];
            const level = kind?.level;
            const showLevel = level && (level.always || action.choice === level.on);
            return (
              <div key={action.applianceId} className="hx-auto-action">
                <div className="hx-auto-action-top">
                  <DeviceIcon device={device} />
                  <div>
                    <p className="hx-auto-action-name">{name}</p>
                    <p className="hx-auto-action-where">{[device?.floor, device?.room].filter(Boolean).join(" | ")}</p>
                    {caps?.confirm ? <p className="hx-auto-action-confirm">Asks to confirm when it runs</p> : null}
                  </div>
                  <button
                    type="button"
                    className="hx-icon-btn hx-auto-remove"
                    onClick={() => remove(action.applianceId)}
                    aria-label={`Remove ${name}`}
                  >
                    <MdiIcon path={mdiClose} size={20} />
                  </button>
                </div>
                <Segmented
                  compact
                  label={`What ${name} does`}
                  options={choices}
                  value={action.choice}
                  onChange={(choice) => update(action.applianceId, { choice })}
                />
                {showLevel ? (
                  <div className="hx-auto-action-level">
                    <p>{level.label}</p>
                    <Chips
                      label={`${name} ${level.label.toLowerCase()}`}
                      options={level.values.map((value) => ({ key: value, label: `${value}${level.unit || ""}` }))}
                      value={action.level ?? level.initial}
                      onChange={(value) => update(action.applianceId, { level: value })}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </section>
      ))}
      {unused.length ? (
        <button type="button" className="hx-auto-add-tile" onClick={() => setPicking(true)}>
          <MdiIcon path={mdiPlusCircleOutline} size={24} />
          Add device
        </button>
      ) : !eligible.length ? (
        <p className="hx-auto-none">None of this home's devices can be used in this kind of automation yet.</p>
      ) : null}
      <DevicePicker open={picking} devices={unused} homeDevice={homeDevice} onAdd={add} onClose={() => setPicking(false)} />
    </>
  );
}
